import { useState, useCallback, useEffect, useRef } from 'react'
import { sync, pushOnly, shouldWarnBeforeUnload, type SyncStatus } from '../db/sync'
import { track, AnalyticsEvents } from '../lib/mixpanel'
import { logger } from '../lib/logger'

const log = logger('Sync')

interface UseSyncOptions {
  userId: string | null
  enabled: boolean
  accessToken: string | null
}

const SYNC_TIMEOUT_MS = 15_000

export function useSync({ userId, enabled, accessToken }: UseSyncOptions) {
  const [status, setStatus] = useState<SyncStatus>('idle')
  // One debounce timer per note. A single shared timer would let an edit to
  // note B cancel a pending push for note A, stranding A's changes.
  const debounceTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const statusResetRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSyncingRef = useRef(false)
  // Set when a sync is requested while another is in flight, so the request is
  // replayed instead of dropped.
  const rerunRef = useRef(false)
  const flushRef = useRef<(() => void) | null>(null)
  const mountEffectCountRef = useRef(0)
  const runSyncVersionRef = useRef(0)

  /**
   * Run a sync operation with shared boilerplate (overlap guard, status
   * management, timeout, analytics, broadcast).
   *
   * @param syncFn  The actual sync work — either full sync or push-only.
   * @param source  Label for analytics / logging.
   */
  const runSync = useCallback(async (
    syncFn: (userId: string, signal: AbortSignal) => Promise<boolean>,
    source: string,
  ) => {
    if (!userId || !enabled) {
      log.warn('runSync SKIPPED: userId=%s enabled=%s (source: %s)', userId ?? 'null', enabled, source)
      return false
    }

    // Prevent overlapping syncs. The request is remembered rather than dropped
    // so that an edit landing mid-sync still reaches the server.
    if (isSyncingRef.current) {
      log.warn('runSync DEFERRED: already in flight (source: %s)', source)
      rerunRef.current = true
      return false
    }

    // Clear any pending status reset from a previous sync
    if (statusResetRef.current) {
      clearTimeout(statusResetRef.current)
      statusResetRef.current = null
    }

    log.warn('runSync START — source=%s, userId=%s, enabled=%s', source, userId, enabled)
    track(AnalyticsEvents.SYNC_TRIGGERED, { source })

    isSyncingRef.current = true
    log.warn('Status -> syncing (source: %s)', source)
    setStatus('syncing')

    let success: boolean
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), SYNC_TIMEOUT_MS)
      try {
        success = await syncFn(userId, controller.signal)
      } finally {
        clearTimeout(timeoutId)
      }

      if (!success) {
        log.warn('Returned failure, source: %s', source)
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        log.error('Timed out after %dms, source: %s', SYNC_TIMEOUT_MS, source)
      } else {
        log.error('Unexpected error in sync:', err)
      }
      success = false
    } finally {
      isSyncingRef.current = false
    }

    const newStatus = success ? 'success' : 'error'
    log.warn('Status -> %s (source: %s)', newStatus, source)
    setStatus(newStatus)

    if (success) {
      log.warn('runSync COMPLETE — success, source=%s', source)
      track(AnalyticsEvents.SYNC_COMPLETED)
      // Reset to idle after 2 seconds (only on success — errors persist
      // until the next sync attempt clears them)
      statusResetRef.current = setTimeout(() => {
        log.warn('Status -> idle (auto-reset after success)')
        setStatus('idle')
        statusResetRef.current = null
      }, 2000)
    } else {
      log.warn('runSync COMPLETE — FAILED, source=%s', source)
      track(AnalyticsEvents.SYNC_FAILED)
    }

    // Replay any request that arrived while this sync was running. A flush
    // covers every dirty note, so one pass settles all deferred requests.
    if (rerunRef.current) {
      rerunRef.current = false
      log.warn('runSync replaying deferred request')
      flushRef.current?.()
    }

    return success
  }, [userId, enabled])

  // Track runSync recreation for diagnostics — only log when deps actually change
  useEffect(() => {
    runSyncVersionRef.current += 1
    log.info('runSync recreated — version=%d, userId=%s, enabled=%s', runSyncVersionRef.current, userId ?? 'null', enabled)
  }, [runSync, userId, enabled])

  // Full sync (pull + push) — used on mount
  const performFullSync = useCallback(
    (source: string = 'mount') => runSync(
      (uid, signal) => sync(uid, signal, accessToken ?? undefined),
      source,
    ),
    [runSync, accessToken],
  )

  // Push-only sync for a specific note — used on edit
  const performPush = useCallback(
    (noteId: string, source: string = 'auto_save') => runSync(
      (uid, signal) => pushOnly(uid, signal, noteId, accessToken ?? undefined),
      source,
    ),
    [runSync, accessToken],
  )

  // Push every dirty note — used to replay requests deferred during a sync
  const performFlush = useCallback(
    () => runSync(
      (uid, signal) => pushOnly(uid, signal, undefined, accessToken ?? undefined),
      'flush',
    ),
    [runSync, accessToken],
  )

  // Held in a ref so runSync can replay without a circular dependency
  useEffect(() => {
    flushRef.current = performFlush
  }, [performFlush])

  // Debounced push for a specific note (1s after last call)
  const debouncedSync = useCallback((noteId: string) => {
    // Don't set unsynced if sync isn't enabled (e.g. auth still loading)
    // or if a sync is already in flight (e.g. mount sync).
    if (!userId || !enabled) {
      log.debug('debouncedSync: sync not enabled, ignoring (userId=%s, enabled=%s)', userId ?? 'null', enabled)
      return
    }

    if (!isSyncingRef.current) {
      // Immediately reflect that local changes haven't been pushed yet.
      if (statusResetRef.current) {
        clearTimeout(statusResetRef.current)
        statusResetRef.current = null
      }
      log.debug('debouncedSync: setting status to unsynced for note %s', noteId.slice(0, 8))
      setStatus('unsynced')
    } else {
      log.debug('debouncedSync: sync in flight, keeping current status')
    }

    // Coalesce rapid edits to the same note, but never cancel another note's
    // pending push.
    const existing = debounceTimersRef.current.get(noteId)
    if (existing) {
      log.debug('debouncedSync: resetting debounce timer for note %s', noteId.slice(0, 8))
      clearTimeout(existing)
    }

    const timers = debounceTimersRef.current
    timers.set(noteId, setTimeout(() => {
      timers.delete(noteId)
      log.debug('debouncedSync: debounce timer fired, pushing note %s', noteId.slice(0, 8))
      performPush(noteId, 'auto_save')
    }, 1000))
  }, [userId, enabled, performPush])

  // Full sync on mount
  useEffect(() => {
    mountEffectCountRef.current += 1
    const fireCount = mountEffectCountRef.current
    log.warn(
      'Mount effect FIRED — count=%d, userId=%s, enabled=%s, isSyncing=%s, currentStatus=%s',
      fireCount, userId ?? 'null', enabled, isSyncingRef.current, status
    )
    if (userId && enabled) {
      log.warn('Mount effect: triggering full sync (fire #%d)', fireCount)
      performFullSync('mount')
    } else {
      log.warn('Mount effect: skipped — not ready (fire #%d, userId=%s, enabled=%s)', fireCount, userId ?? 'null', enabled)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- status is intentionally excluded to prevent re-triggering sync on status changes
  }, [userId, enabled, performFullSync])

  // Warn user if they try to close the browser with unsynced or in-flight changes
  useEffect(() => {
    if (!shouldWarnBeforeUnload(status)) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [status])

  // Cleanup on unmount
  useEffect(() => {
    const timers = debounceTimersRef.current
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer)
      }
      timers.clear()
      if (statusResetRef.current) {
        clearTimeout(statusResetRef.current)
      }
    }
  }, [])

  return {
    status,
    sync: performFullSync,
    debouncedSync,
  }
}
